import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import { useSaveActionsContext } from "context/save";

import usePageApi from "hooks/api/private/usePageApi";

import { LanguageEnum, MetaFields } from "types/api/commons";
import { PageBody, PageDetails } from "types/api/page";

import { emptyMetaFields, metaFieldsSchema } from "utils/metaFields";

export type PageBodyForm = {
    name: string;
    theme: string | null;
    text: MetaFields;
};

export type PageFormHook = {
    formHook: FormHookType<PageBodyForm>;
};

function usePageForm({ pageId }: { pageId?: string }): PageFormHook {
    const { createPage, updatePage, getOnePage } = usePageApi();

    const { addSaveAction } = useSaveActionsContext();

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                name: {
                    type: "simple",
                    validators: [notEmptyValidator],
                },
                theme: {
                    type: "simple",
                    validators: [],
                },
                text: metaFieldsSchema,
            },
        };
    }, []);

    const initialValues: PageBodyForm = React.useMemo(() => {
        return {
            name: "",
            theme: "",
            text: emptyMetaFields(),
        };
    }, []);

    const formHook = useForm<PageBodyForm>(schema, initialValues);

    const { showAllError, setForm, isValid, form } = formHook;

    const [pageDetails, setPageDetails] = React.useState<PageDetails | undefined>(undefined);

    React.useEffect(() => {
        if (pageId) {
            const [apiCall, abortController] = getOnePage({ id: pageId });

            apiCall.then(setPageDetails);

            return () => abortController.abort();
        }
    }, [getOnePage, pageId]);

    React.useEffect(() => {
        if (pageDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.name = pageDetails.name;
                newForm.theme = pageDetails.theme ?? "";

                newForm.text[LanguageEnum.it] = {
                    meta_title: pageDetails.text?.it?.meta_title ?? "",
                    meta_description: pageDetails.text?.it?.meta_description ?? "",
                    meta_keywords: pageDetails.text?.it?.meta_keywords ?? "",
                };

                newForm.text[LanguageEnum.en] = {
                    meta_title: pageDetails.text?.en?.meta_title ?? "",
                    meta_description: pageDetails.text?.en?.meta_description ?? "",
                    meta_keywords: pageDetails.text?.en?.meta_keywords ?? "",
                };

                return newForm;
            });
        }
    }, [setForm, pageDetails]);

    React.useEffect(() => {
        addSaveAction("usePageForm", () => {
            if (isValid) {
                const promises: Promise<unknown>[] = [];

                const body: PageBody = {
                    name: form.name,
                    theme: form.theme,
                    text: {
                        [LanguageEnum.it]: { ...form.text[LanguageEnum.it] },
                        [LanguageEnum.en]: { ...form.text[LanguageEnum.en] },
                    },
                };

                if (pageId) {
                    const [apiCall] = updatePage({ id: pageId }, body);

                    promises.push(apiCall);
                } else {
                    const [apiCall] = createPage(body);

                    promises.push(apiCall);
                }

                return Promise.all(promises);
            } else {
                showAllError();

                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        addSaveAction,
        showAllError,
        createPage,
        updatePage,
        form.name,
        form.theme,
        form.text,
        pageId,
        isValid,
    ]);

    return React.useMemo(
        (): PageFormHook => ({
            formHook,
        }),
        [formHook],
    );
}

export default usePageForm;
